import React, { useCallback, useMemo } from 'react';
import { useSearchParams } from 'next/navigation';
import { useInternalPathname } from 'hooks/navigation/useInternalPathname';
import { useAffiliateMetadata } from 'utils/affiliateHelpers';
import {
  referralCodes,
  ringReferralCodes,
  ringProReferralCodes,
  getRingPromoDiscountConfig,
  getHomePromoDiscountConfig,
} from 'content/referral';
import { Campaign, getCampaignByProduct } from 'content/campaigns';
import { isM1UhxBundlingEnabled } from 'content/products/m1/m1UhxBundlingConfig';
import { ultrahumanXDiscountCodes } from 'content/ultrahumanX';
import {
  ClueShopifyMap,
  isClueAvailable,
} from 'content/products/addons/clue';
import { getHeroDiscountCode, HeroCategoriesMap } from '../utils';
import { isGovxEligible } from 'components/heroes/govxConfig';
import { ProductType } from '../types';
import {
  ShopifyStore,
  getEffectiveStoreForDiscount,
} from 'lib/commerce/shopify/shopifyHelper';
import { DiscountManager } from './types';
import { useRegionLocale } from 'contexts/RegionLocaleContext';

export const useDiscountManager = (
  store: ShopifyStore | null,
  cartType: ProductType | undefined,
  ultrahumanXAdded: any,
  cart: Record<string, any> | null,
  discountCodesRef: React.MutableRefObject<Array<string> | []>,
  campaigns: Campaign[] = [],
): DiscountManager => {
  const searchParams = useSearchParams();
  const internalPathname = useInternalPathname();
  const affiliateCode = searchParams?.get('affiliateCode');
  const { region: country } = useRegionLocale();

  // Don't load affiliate details if it's just a birthday discount without a valid discount parameter
  const shouldLoadAffiliateDetails =
    affiliateCode &&
    !(affiliateCode === 'birthday' && !searchParams?.get('discount'));

  const { affiliateDetails } = useAffiliateMetadata(
    shouldLoadAffiliateDetails ? (affiliateCode as string) : '',
  );

  const campaignActive = false; // Change when adding a campaign for uhx

  const setDiscountCodes = useCallback(
    (
      initialDiscounts: Array<string>,
      options: {
        isM1ProductTypePresent?: boolean;
        isRingProductTypePresent?: boolean;
        isRingProProductTypePresent?: boolean;
        isUhxProductTypePresent?: boolean;
        removingUhx?: boolean;
        hasTradeIn?: boolean;
      } = {},
    ): Array<string> => {
      if (!store) {
        discountCodesRef.current = [];
        return discountCodesRef.current;
      }

      const {
        isM1ProductTypePresent,
        isRingProductTypePresent,
        isRingProProductTypePresent,
        isUhxProductTypePresent,
        removingUhx = false,
        hasTradeIn = false,
      } = options;

      let discounts: Array<string> = [...initialDiscounts];

      // Add discount from URL query
      const queryDiscount = searchParams?.get('discount');
      if (queryDiscount) {
        discounts.push(queryDiscount);
      }

      const referral = searchParams?.get('referral') ?? '';

      // Handle referral codes
      if (referral) {
        if (
          cartType === ProductType.RING_PRO ||
          isRingProProductTypePresent ||
          internalPathname.startsWith('/ring-pro')
        ) {
          const coupon =
            ringProReferralCodes[store as ShopifyStore]?.discountCode;
          if (coupon) {
            discounts = discounts.filter(
              (x) =>
                x !== ringReferralCodes[store as ShopifyStore]?.discountCode &&
                x !== referralCodes[store as ShopifyStore]?.discountCode,
            );
            discounts.push(coupon);
          }
        } else if (cartType === ProductType.RING || isRingProductTypePresent) {
          const coupon = ringReferralCodes[store as ShopifyStore].discountCode;
          discounts = discounts.filter(
            (x) => x !== referralCodes[store as ShopifyStore]?.discountCode,
          );
          discounts.push(coupon);
        } else if (cartType === ProductType.M1 || isM1ProductTypePresent) {
          const coupon =
            referralCodes[store as ShopifyStore]?.discountCode ?? '';
          discounts = discounts.filter(
            (x) => x !== ringReferralCodes[store as ShopifyStore].discountCode,
          );
          discounts.push(coupon);
        }
      } else if (affiliateDetails) {
        // Handle affiliate discounts — only push the code for the current product
        const effectiveStore = getEffectiveStoreForDiscount(store);

        const discountConfigs =
          affiliateDetails.discount_configs[effectiveStore as ShopifyStore] ??
          {};

        // Map current product context to the affiliate discount config key
        let affiliateCode_: string | undefined;
        if (cartType === ProductType.RING_PRO || isRingProProductTypePresent || internalPathname.startsWith('/ring-pro')) {
          affiliateCode_ = discountConfigs['RING_PRO']?.code;
        } else if (cartType === ProductType.RING || isRingProductTypePresent || internalPathname.startsWith('/ring')) {
          affiliateCode_ = discountConfigs['RING']?.code;
        } else if (cartType === ProductType.M1 || isM1ProductTypePresent || internalPathname.startsWith('/blood')) {
          affiliateCode_ = discountConfigs['M1']?.code;
        } else if (internalPathname.startsWith('/home')) {
          affiliateCode_ = discountConfigs['HOME']?.code;
        }
        if (affiliateCode_) {
          discounts.push(affiliateCode_);
        }
      }

      const m1UhxBundlingEnabled = isM1UhxBundlingEnabled(store);

      // Handle UHX discounts for M1 + UHX co-presence
      if (
        m1UhxBundlingEnabled &&
        (ultrahumanXAdded || isUhxProductTypePresent) &&
        (cartType === ProductType.M1 || isM1ProductTypePresent) &&
        !removingUhx &&
        store
      ) {
        const uhxCode = ultrahumanXDiscountCodes[store]?.discountCode;
        if (uhxCode && !discounts.includes(uhxCode)) {
          discounts.push(uhxCode);
        }
      } else if (store && removingUhx) {
        const uhxCode = ultrahumanXDiscountCodes[store]?.discountCode;
        if (uhxCode) {
          discounts = discounts.filter((x) => x !== uhxCode);
        }
      }

      // Handle hero category discounts
      const heroCategory = searchParams?.get('heroCategory') ?? '';
      if (heroCategory) {
        const blockVeteransForGovx =
          heroCategory === HeroCategoriesMap.VETERANS && isGovxEligible(country);
        if (!blockVeteransForGovx) {
          const heroCode = getHeroDiscountCode(heroCategory);
          if (heroCode) discounts.push(heroCode);
        }
      }

      // Handle birthday discount
      const birthdayDiscountCode = searchParams?.get('discount') ?? '';
      if (birthdayDiscountCode) {
        discounts.push(birthdayDiscountCode);
      }

      // Handle Clue discount
      const isClueFlow =
        searchParams?.get('flow') === 'clue' && isClueAvailable(store);
      if (isClueFlow && store && ClueShopifyMap[store]?.Year1?.discountCode) {
        discounts.push(ClueShopifyMap[store].Year1.discountCode);
      }

      // DISABLED: Respiratory Health PowerPlug discount — re-enable by uncommenting
      // // Handle Respiratory discount
      // const isRespiratoryFlow = searchParams?.get('flow') === 'respiratory';
      // if (
      //   isRespiratoryFlow &&
      //   store &&
      //   RespiratoryShopifyMap[store]?.Year1?.discountCode
      // ) {
      //   discounts.push(RespiratoryShopifyMap[store].Year1.discountCode);
      // }

      const promoCountry = searchParams?.get('locale') ?? country;
      const ringPromoConfig = getRingPromoDiscountConfig({
        store,
        country: promoCountry,
      });

      if (
        store &&
        (cartType === ProductType.RING || isRingProductTypePresent) &&
        ringPromoConfig?.active &&
        ringPromoConfig?.discountCode
      ) {
        // const coupon = ringPromoConfig.discountCode;
        // if (coupon && !discounts.includes(coupon)) {
        //   discounts.push(coupon);
        // }
      }

      // --- Home Promo Discount ---
      const homePromoConfig = getHomePromoDiscountConfig({
        store,
        country: searchParams?.get('country'),
      });

      if (
        internalPathname.startsWith('/home') &&
        homePromoConfig?.active &&
        homePromoConfig.discountCode
      ) {
        // const coupon = homePromoConfig.discountCode;
        // if (coupon && !discounts.includes(coupon)) {
        //   discounts.push(coupon);
        // }
      }

      // --- Campaign Discount Codes ---
      const ringCampaign = getCampaignByProduct(campaigns, 'ring');
      if (
        ringCampaign &&
        !hasTradeIn &&
        (cartType === ProductType.RING || isRingProductTypePresent)
      ) {
        const coupon = ringCampaign.discount_code;
        if (coupon && !discounts.includes(coupon)) {
          discounts.push(coupon);
        }
      }

      const ringProCampaign = getCampaignByProduct(campaigns, 'ring-pro');
      if (
        ringProCampaign &&
        !hasTradeIn &&
        (cartType === ProductType.RING_PRO || isRingProProductTypePresent)
      ) {
        const coupon = ringProCampaign.discount_code;
        if (coupon && !discounts.includes(coupon)) {
          discounts.push(coupon);
        }
      }

      const homeCampaign = getCampaignByProduct(campaigns, 'home');
      if (homeCampaign && internalPathname.startsWith('/home')) {
        const coupon = homeCampaign.discount_code;
        if (coupon && !discounts.includes(coupon)) {
          discounts.push(coupon);
        }
      }

      const m1Campaign = getCampaignByProduct(campaigns, 'cgm');
      if (
        m1Campaign &&
        (cartType === ProductType.M1 || isM1ProductTypePresent) &&
        !removingUhx &&
        (ultrahumanXAdded || isUhxProductTypePresent || !m1UhxBundlingEnabled)
      ) {
        const coupon = m1Campaign.discount_code;
        if (coupon && !discounts.includes(coupon)) {
          discounts.push(coupon);
        }
      }

      // Remove duplicates and empty strings
      discountCodesRef.current = [...new Set(discounts.filter((x) => x))];

      return discountCodesRef.current;
    },
    [
      searchParams,
      internalPathname,
      store,
      cartType,
      cart,
      ultrahumanXAdded,
      affiliateDetails,
      campaignActive,
      country,
      campaigns,
    ],
  );

  const applicableDiscountCoupons = useMemo(
    () =>
      cart?.discountCodes
        ?.filter((x: { applicable: boolean }) => x.applicable)
        ?.map((x: { code: boolean }) => x.code) ?? [],
    [cart?.discountCodes],
  );

  return {
    discountCodes: discountCodesRef.current,
    setDiscountCodes,
    applicableDiscountCoupons,
  };
};
